import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  Stack,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Text,
  DatePicker,
  Icon,
} from '@fluentui/react'
import { TrendChart, ApplicationChangesChart } from '../components/charts'
import { apiService } from '../services/apiService'
import { useApi } from '../hooks/useApi'
import type { TrendData, CACoverageData, CACoverageApp } from '../types'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

const dateRangeOptions: IDropdownOption[] = [
  { key: '7', text: 'Last 7 days' },
  { key: '30', text: 'Last 30 days' },
  { key: '90', text: 'Last 90 days' },
  { key: '180', text: 'Last 6 months' },
  { key: '365', text: 'Last year' },
  { key: 'custom', text: 'Custom range' },
]

const intervalOptions: IDropdownOption[] = [
  { key: 'daily', text: 'Daily' },
  { key: 'weekly', text: 'Weekly' },
  { key: 'monthly', text: 'Monthly' },
]

const COVERAGE_COLORS: Record<string, string> = {
  Covered: '#107c10',
  ReportOnly: '#ca5010',
  NotCovered: '#a4262c',
}

function CoverageBadge({ status }: { status: string }) {
  const color = COVERAGE_COLORS[status] ?? '#605e5c'
  const label = status === 'ReportOnly' ? 'Report Only' : status === 'NotCovered' ? 'Not Covered' : status
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 600,
      color: '#fff',
      backgroundColor: color,
    }}>
      {label}
    </span>
  )
}

function CoverageTable({
  title,
  apps,
  accentColor,
  expanded,
  onToggle,
}: {
  title: string
  apps: CACoverageApp[]
  accentColor: string
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          width: '100%',
          textAlign: 'left',
        }}
      >
        <span style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: accentColor,
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 15, fontWeight: 600, color: '#323130', flex: 1 }}>
          {title}
          <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#605e5c' }}>
            ({apps.length})
          </span>
        </span>
        <Icon iconName={expanded ? 'ChevronUp' : 'ChevronDown'} styles={{ root: { color: '#605e5c', fontSize: 12 } }} />
      </button>

      {expanded && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          {apps.length === 0 ? (
            <Text styles={{ root: { color: '#a19f9d', fontSize: 13, padding: '8px 0' } }}>
              No applications in this category.
            </Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #edebe9' }}>
                  {['Application', 'SSO Mode', 'Total Policies', 'MFA Policies', 'Status'].map(h => (
                    <th key={h} style={{
                      padding: '6px 12px',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: '#605e5c',
                      whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apps.map((app, i) => (
                  <tr
                    key={app.applicationId}
                    style={{ backgroundColor: i % 2 === 0 ? 'transparent' : '#faf9f8' }}
                  >
                    <td style={{ padding: '7px 12px', color: '#323130', fontWeight: 500 }}>
                      {app.displayName}
                    </td>
                    <td style={{ padding: '7px 12px', color: '#605e5c' }}>
                      {app.ssoMode || '—'}
                    </td>
                    <td style={{ padding: '7px 12px', color: '#605e5c', textAlign: 'center' }}>
                      {app.totalPolicies}
                    </td>
                    <td style={{ padding: '7px 12px', color: '#605e5c', textAlign: 'center' }}>
                      {app.coveringPolicies.length}
                    </td>
                    <td style={{ padding: '7px 12px' }}>
                      <CoverageBadge status={app.coverageStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export function Reports() {
  const [dateRange, setDateRange] = useState('30')
  const [interval, setInterval] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>(undefined)
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>(undefined)
  const [isExporting, setIsExporting] = useState(false)
  const chartsRef = useRef<HTMLDivElement>(null)

  // CA Coverage expand/collapse state — not covered open by default, others collapsed
  const [notCoveredExpanded, setNotCoveredExpanded] = useState(true)
  const [reportOnlyExpanded, setReportOnlyExpanded] = useState(false)
  const [coveredExpanded, setCoveredExpanded] = useState(false)

  const fetchTrends = useCallback(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return apiService.getApplicationTrends({
        startDate: customStartDate.toISOString(),
        endDate: customEndDate.toISOString(),
        interval,
      })
    }
    const days = dateRange === 'custom' ? 30 : parseInt(dateRange)
    const endDate = new Date().toISOString()
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
    return apiService.getApplicationTrends({ startDate, endDate, interval })
  }, [dateRange, interval, customStartDate, customEndDate])

  const fetchCACoverage = useCallback(() => apiService.getCACoverage(), [])

  // Auto-adjust interval when preset range changes (custom leaves it user-controlled)
  useEffect(() => {
    if (dateRange === 'custom') return
    const days = parseInt(dateRange)
    if (days <= 30) setInterval('daily')
    else if (days <= 180) setInterval('weekly')
    else setInterval('monthly')
  }, [dateRange])

  // Effective days for the ApplicationChangesChart (NaN-safe for custom mode)
  const effectiveDays = useMemo(() => {
    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        return Math.max(1, Math.ceil((customEndDate.getTime() - customStartDate.getTime()) / 86_400_000))
      }
      return 30
    }
    return parseInt(dateRange)
  }, [dateRange, customStartDate, customEndDate])

  const { data: trends, loading, error, refetch } = useApi<TrendData>(
    fetchTrends,
    [dateRange, interval, customStartDate, customEndDate],
  )
  const { data: caCoverage, loading: caLoading, error: caError, refetch: refetchCoverage } = useApi<CACoverageData>(fetchCACoverage)

  const handleExportPdf = async () => {
    if (!chartsRef.current || !trends) return

    setIsExporting(true)
    try {
      const canvas = await html2canvas(chartsRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      // Title
      pdf.setFontSize(20)
      pdf.setTextColor(50, 49, 48)
      pdf.text('Application Trends Report', 14, 20)

      // Date range
      pdf.setFontSize(12)
      pdf.setTextColor(96, 94, 92)
      pdf.text(`Period: ${trends.period.start} to ${trends.period.end}`, 14, 28)
      pdf.text(`Interval: ${trends.interval}`, 14, 34)

      // Summary section
      pdf.setFontSize(14)
      pdf.setTextColor(50, 49, 48)
      pdf.text('Summary', 14, 46)

      pdf.setFontSize(11)
      pdf.setTextColor(96, 94, 92)
      pdf.text(`SSO Adoption Change: ${trends.summary.ssoAdoptionChange}`, 14, 54)
      pdf.text(`Ownership Coverage Change: ${trends.summary.ownershipCoverageChange}`, 14, 60)
      pdf.text(`Average Credential Health: ${trends.summary.averageCredentialHealth}%`, 14, 66)

      // Charts image
      const imgWidth = pdfWidth - 28
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      const yPosition = 76

      if (yPosition + imgHeight > pdfHeight - 20) {
        const scaleFactor = (pdfHeight - yPosition - 20) / imgHeight
        pdf.addImage(imgData, 'PNG', 14, yPosition, imgWidth * scaleFactor, imgHeight * scaleFactor)
      } else {
        pdf.addImage(imgData, 'PNG', 14, yPosition, imgWidth, imgHeight)
      }

      // Footer
      pdf.setFontSize(8)
      pdf.setTextColor(160, 160, 160)
      pdf.text(`Generated on ${new Date().toLocaleString()}`, 14, pdfHeight - 10)

      pdf.save(`application-trends-${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (err) {
      console.error('Failed to export PDF:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportCsv = () => {
    if (!trends?.dataPoints.length) return
    const headers = ['Date', 'Total Applications', 'SSO Enabled', 'SSO Disabled', 'With Owner', 'Without Owner', 'Credentials Healthy', 'Expiring Soon', 'Expired']
    const rows = trends.dataPoints.map(dp => [
      dp.date, dp.totalApplications, dp.ssoEnabled, dp.ssoDisabled,
      dp.withOwner, dp.withoutOwner, dp.credentialsHealthy,
      dp.credentialsExpiringSoon, dp.credentialsExpired,
    ])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `ssops-trends-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExportCACoverageCsv = () => {
    if (!caCoverage?.applications.length) return
    const headers = ['App Name', 'App ID', 'SSO Mode', 'Coverage Status', 'Total Policies', 'MFA Policies', 'Policy Names']
    const rows = caCoverage.applications.map(app => [
      `"${app.displayName.replace(/"/g, '""')}"`,
      app.appId,
      app.ssoMode || '',
      app.coverageStatus,
      app.totalPolicies,
      app.coveringPolicies.length,
      `"${app.policies.map(p => p.displayName).join('; ').replace(/"/g, '""')}"`,
    ])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `ssops-ca-coverage-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const hasData = trends && trends.dataPoints && trends.dataPoints.length > 0

  const notCoveredApps = caCoverage?.applications.filter(a => a.coverageStatus === 'NotCovered') ?? []
  const reportOnlyApps = caCoverage?.applications.filter(a => a.coverageStatus === 'ReportOnly') ?? []
  const coveredApps = caCoverage?.applications.filter(a => a.coverageStatus === 'Covered') ?? []

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-description">
          Application trends and analytics over time
        </p>
      </div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 24 }}>
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end" wrap>
            <Dropdown
              label="Date Range"
              selectedKey={dateRange}
              options={dateRangeOptions}
              onChange={(_, option) => option && setDateRange(option.key as string)}
              styles={{ dropdown: { width: 150 } }}
            />
            <Dropdown
              label="Interval"
              selectedKey={interval}
              options={intervalOptions}
              onChange={(_, option) => option && setInterval(option.key as 'daily' | 'weekly' | 'monthly')}
              styles={{ dropdown: { width: 120 } }}
            />
            <DefaultButton
              text="Refresh"
              iconProps={{ iconName: 'Refresh' }}
              onClick={() => refetch()}
              disabled={loading}
            />
            <DefaultButton
              text="Export CSV"
              iconProps={{ iconName: 'Download' }}
              onClick={handleExportCsv}
              disabled={loading || !hasData}
            />
            <PrimaryButton
              text={isExporting ? 'Exporting...' : 'Export PDF'}
              iconProps={{ iconName: 'PDF' }}
              onClick={handleExportPdf}
              disabled={loading || isExporting || !hasData}
            />
          </Stack>
          {dateRange === 'custom' && (
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end" wrap>
              <DatePicker
                label="Start date"
                value={customStartDate}
                onSelectDate={date => setCustomStartDate(date ?? undefined)}
                maxDate={customEndDate ?? new Date()}
                styles={{ root: { width: 160 } }}
              />
              <DatePicker
                label="End date"
                value={customEndDate}
                onSelectDate={date => setCustomEndDate(date ?? undefined)}
                minDate={customStartDate}
                maxDate={new Date()}
                styles={{ root: { width: 160 } }}
              />
            </Stack>
          )}
        </Stack>
      </div>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 16 } }}>
          {error}
        </MessageBar>
      )}

      {loading ? (
        <Stack horizontalAlign="center" styles={{ root: { padding: 60 } }}>
          <Spinner size={SpinnerSize.large} label="Loading trend data..." />
        </Stack>
      ) : !hasData ? (
        <div className="card">
          <Stack horizontalAlign="center" styles={{ root: { padding: 60 } }}>
            <Text variant="large" styles={{ root: { color: '#605e5c' } }}>
              No snapshot data available for the selected period.
            </Text>
            <Text styles={{ root: { color: '#a19f9d', marginTop: 8 } }}>
              Application snapshots are created during sync operations.
            </Text>
          </Stack>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card" style={{ borderLeft: '4px solid #0078d4' }}>
              <div className="stat-value" style={{ color: '#0078d4' }}>
                {trends.summary.ssoAdoptionChange}
              </div>
              <div className="stat-label">SSO Adoption Change</div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #107c10' }}>
              <div className="stat-value" style={{ color: '#107c10' }}>
                {trends.summary.ownershipCoverageChange}
              </div>
              <div className="stat-label">Ownership Coverage Change</div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #8764b8' }}>
              <div className="stat-value" style={{ color: '#8764b8' }}>
                {trends.summary.averageCredentialHealth}%
              </div>
              <div className="stat-label">Avg Credential Health</div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #605e5c' }}>
              <div className="stat-value" style={{ color: '#605e5c' }}>
                {trends.dataPoints[trends.dataPoints.length - 1]?.totalApplications ?? '—'}
              </div>
              <div className="stat-label">Current Total Apps</div>
            </div>
          </div>

          {/* Charts */}
          <div ref={chartsRef}>
            {/* Application Change History */}
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600, color: '#323130' }}>
                Application Change History
              </h3>
              <ApplicationChangesChart days={effectiveDays} showSummary={false} />
            </div>

            {/* SSO Adoption Trend */}
            <div className="card" style={{ marginBottom: 24 }}>
              <TrendChart
                title="SSO Adoption Trend"
                data={trends.dataPoints}
                xKey="date"
                type="line"
                height={300}
                lines={[
                  { dataKey: 'totalApplications', name: 'Total Applications', color: '#605e5c' },
                  { dataKey: 'ssoEnabled', name: 'SSO Enabled', color: '#107c10' },
                ]}
              />
            </div>

            {/* Ownership Coverage */}
            <div className="card" style={{ marginBottom: 24 }}>
              <TrendChart
                title="Ownership Coverage"
                data={trends.dataPoints}
                xKey="date"
                type="area"
                height={300}
                lines={[
                  { dataKey: 'withOwner', name: 'With Owner', color: '#107c10' },
                  { dataKey: 'withoutOwner', name: 'Without Owner', color: '#d83b01' },
                ]}
              />
            </div>

            {/* Credential Health */}
            <div className="card" style={{ marginBottom: 24 }}>
              <TrendChart
                title="Credential Health"
                data={trends.dataPoints}
                xKey="date"
                type="bar"
                height={300}
                lines={[
                  { dataKey: 'credentialsHealthy', name: 'Healthy', color: '#107c10' },
                  { dataKey: 'credentialsExpiringSoon', name: 'Expiring Soon', color: '#ffaa44' },
                  { dataKey: 'credentialsExpired', name: 'Expired', color: '#a4262c' },
                ]}
              />
            </div>
          </div>
        </>
      )}

      {/* ── CA Policy Coverage ────────────────────────────────── */}
      <div style={{ marginTop: 40, borderTop: '1px solid #edebe9', paddingTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#323130' }}>
              CA Policy Coverage
            </h2>
            <p style={{ margin: '4px 0 0', color: '#605e5c', fontSize: 13 }}>
              Per-app MFA enforcement analysis based on last sync data
            </p>
          </div>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <DefaultButton
              text="Refresh"
              iconProps={{ iconName: 'Refresh' }}
              onClick={() => refetchCoverage()}
              disabled={caLoading}
            />
            <DefaultButton
              text="Export CSV"
              iconProps={{ iconName: 'Download' }}
              onClick={handleExportCACoverageCsv}
              disabled={caLoading || !caCoverage?.applications.length}
            />
          </Stack>
        </div>

        {caError && (
          <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 16 } }}>
            {caError}
          </MessageBar>
        )}

        {caLoading ? (
          <Stack horizontalAlign="center" styles={{ root: { padding: 48 } }}>
            <Spinner size={SpinnerSize.large} label="Loading coverage data..." />
          </Stack>
        ) : caCoverage && (
          <>
            {/* Summary tiles */}
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card" style={{ borderLeft: '4px solid #a4262c' }}>
                <div className="stat-value" style={{ color: '#a4262c' }}>{caCoverage.summary.notCovered}</div>
                <div className="stat-label">Not Covered</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #ca5010' }}>
                <div className="stat-value" style={{ color: '#ca5010' }}>{caCoverage.summary.reportOnly}</div>
                <div className="stat-label">Report Only</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #107c10' }}>
                <div className="stat-value" style={{ color: '#107c10' }}>{caCoverage.summary.covered}</div>
                <div className="stat-label">Covered</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #605e5c' }}>
                <div className="stat-value" style={{ color: '#605e5c' }}>{caCoverage.summary.total}</div>
                <div className="stat-label">Total Apps</div>
              </div>
            </div>

            {/* Per-status tables */}
            <CoverageTable
              title="Not Covered"
              apps={notCoveredApps}
              accentColor="#a4262c"
              expanded={notCoveredExpanded}
              onToggle={() => setNotCoveredExpanded(v => !v)}
            />
            <CoverageTable
              title="Report Only"
              apps={reportOnlyApps}
              accentColor="#ca5010"
              expanded={reportOnlyExpanded}
              onToggle={() => setReportOnlyExpanded(v => !v)}
            />
            <CoverageTable
              title="Covered"
              apps={coveredApps}
              accentColor="#107c10"
              expanded={coveredExpanded}
              onToggle={() => setCoveredExpanded(v => !v)}
            />

            {/* Disclaimer */}
            <MessageBar
              messageBarType={MessageBarType.info}
              styles={{ root: { marginTop: 8 } }}
            >
              {caCoverage.summary.disclaimer}
            </MessageBar>
          </>
        )}
      </div>
    </div>
  )
}
